let verticalLine, tooltip

/**
 * This plugin requires that the following be added to the chart's `plugins` options:
 * 
 * lockedDate: {
    getLockedDate: () => this.chartLockedDate, // `chartLockedDate` should be passed to the chart component as a prop.
    displayPrice: false, // Flag to determine if the price annotation should be displayed.
 * }
 */

export default {
  id: 'lockedDate',
  events: ['click', 'mousemove'],
  /**
   * Draw the vertical line and price tooltip before
   * the datasets are drawn.
   */
  beforeDatasetsDraw(chart, args) {
    drawVerticalLine(chart, args)
    drawTooltip(chart, args)
  },
  /**
   * Draw the locked date after the chart is drawn.
   */
  afterDraw(chart) {
    drawLockedDate(chart)
  },
  /**
   * Position the annotations on `mousemove` or `click`.
   */
  afterEvent(chart, args) {
    const event = args.event
    const { ctx, canvas, chartArea: { left, right, top, bottom } } = chart

    // Mouse: position the vertical line and tooltip.
    if (event.type == 'mousemove') {

      const xCoord = args.event.x
      const yCoord = args.event.y
      const points = chart.getElementsAtEventForMode(args.event, 'index', { intersect: false }, false)

      // Position the vertical line.
      if (!args.inChartArea && verticalLine) {
        verticalLine = undefined
        args.changed = true

      } else if (args.inChartArea) {
        verticalLine = {
          startX: xCoord,
          startY: top,
          endX: xCoord,
          endY: bottom,
        }
        args.changed = true
      }

      // Position the tooltip.
      if (!args.inChartArea && tooltip) {
        tooltip = undefined
        args.changed = true

      } else if (args.inChartArea) {
        tooltip = {
          x: xCoord,
          y: bottom,
          point: points[0],
        }
        args.changed = true
      }

    // Click: annotate the chart with the locked date.
    } else if (event.type == 'click') {

      const points = chart.getElementsAtEventForMode(event, 'index', { intersect: false }, false)
      if (points[0]) {

        // Notify the chart component of the clicked date.
        canvas.dispatchEvent(new CustomEvent('lockedDate', {
          detail: {
            date: chart.data.labels[ points[0].index ],
          }
        }))

        args.changed = true
      }
    }
  }
}

/**
 * Determine the point index for a date.
 */
const getPointIndexByDate = (chart, date) => {
  let index

  chart.data.labels.forEach((labelDate, labelIndex) => {
    if (date === labelDate) {
      index = labelIndex
      return
    }
  })

  return index
}

// Draw the vertical line.
const drawVerticalLine = (chart, args) => {
  const { ctx } = chart

  if (verticalLine) {
    ctx.save()
    ctx.beginPath()
    ctx.lineWidth = 1
    ctx.strokeStyle = '#b6b6b6'
    ctx.setLineDash([3, 3]);
    ctx.moveTo(verticalLine.startX, verticalLine.startY)
    ctx.lineTo(verticalLine.endX, verticalLine.endY)
    ctx.stroke()
    ctx.restore()
  }
}

// Draw the "Click to Lock Date" tooltip.
const drawTooltip = (chart, args) => {
  const { ctx } = chart

  if (tooltip) {
    const rectWidth = 130
    const rectHeight = 55
    const rectX = tooltip.x - (rectWidth / 2)
    const rectY = tooltip.y

    const label = chart?.data?.labels?.[tooltip?.point?.index] ||  undefined
    if (! label) {
      return
    }

    // Rounded rectangle
    ctx.save()
    ctx.beginPath()
    ctx.fillStyle = '#5669a4'
    ctx.roundRect(rectX, rectY, rectWidth, rectHeight, [5])
    ctx.fill()
    ctx.restore()

    // Caret
    ctx.save()
    ctx.translate(rectX + (rectWidth / 2), rectY - 6)
    ctx.rotate((45 * Math.PI) / 180)
    ctx.beginPath()
    ctx.fillStyle = '#5669a4'
    ctx.rect(0, 0, 12, 12)
    ctx.fill()
    ctx.restore()

    // Date
    ctx.save()
    ctx.beginPath()
    ctx.font = '600 .75rem "Open Sans", sans-serif'
    ctx.textAlign = 'center' 
    ctx.textBaseline = 'middle'
    ctx.fillStyle = '#ffffff'
    ctx.fillText(chart.data.labels[tooltip.point.index], rectX + (rectWidth / 2), rectY + 20)
    ctx.stroke()

    // Label (Click to Lock Date)
    ctx.save()
    ctx.beginPath()
    ctx.font = '.75rem "Open Sans", sans-serif'
    ctx.textAlign = 'center' 
    ctx.textBaseline = 'middle'
    ctx.fillStyle = '#ffffff'
    ctx.fillText('Click to Lock Date', rectX + (rectWidth / 2), rectY + 36)
    ctx.stroke()
    ctx.restore()
  }
}

// Draw the locked date.
const drawLockedDate = (chart) => {
  
  // Should we display the price?
  const displayPrice = chart.config._config.options.plugins?.lockedDate?.displayPrice || false

  // Get the locked date.
  const getLockedDate = chart.config._config.options.plugins?.lockedDate?.getLockedDate || undefined
  const lockedDate = getLockedDate()
  if (!lockedDate) {
    return
  }

  // Get the x coordinate of the locked date by its index.
  const lockedDateIndex = getPointIndexByDate(chart, lockedDate)
  const lockedDateX = chart.scales.x.getPixelForValue(lockedDateIndex)

  // Chart data
  const { ctx, chartArea: { left, right, top, bottom } } = chart

  // Date rect
  const dateRectWidth = 100
  const dateRectHeight = 29
  const dateRectX = lockedDateX - (dateRectWidth / 2)

  // Vertical line
  ctx.save()
  ctx.beginPath()
  ctx.lineWidth = 1
  ctx.strokeStyle = '#b6b6b6'
  ctx.setLineDash([3, 3]);
  ctx.moveTo(lockedDateX, top)
  ctx.lineTo(lockedDateX, bottom)
  ctx.stroke()
  ctx.restore()

  // Date rounded rectangle
  ctx.save()
  ctx.beginPath()
  ctx.fillStyle = '#294289'
  ctx.roundRect(dateRectX, 5, dateRectWidth, dateRectHeight, [5])
  ctx.fill()
  ctx.restore()

  // Date
  ctx.save()
  ctx.beginPath()
  ctx.font = '600 .75rem "Open Sans", sans-serif'
  ctx.textAlign = 'center' 
  ctx.textBaseline = 'middle'
  ctx.fillStyle = '#ffffff'
  ctx.fillText('Date: ' + chart.data.labels[ lockedDateIndex ], dateRectX + (dateRectWidth / 2), 20)
  ctx.stroke()
  ctx.restore()

  // Price
  if (displayPrice) {

    // Price rect
    const priceRectWidth = 80
    const priceRectHeight = 29
    const priceRectX = lockedDateX - (priceRectWidth / 2)

    // Rounded rectangle
    ctx.save()
    ctx.beginPath()
    ctx.fillStyle = '#ffffff'
    ctx.strokeStyle = '#e3e3e3'
    ctx.roundRect(priceRectX, priceRectHeight + 8, priceRectWidth, priceRectHeight, [5])
    ctx.fill()
    ctx.stroke()
    ctx.restore()

    // Price
    const price = new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
    }).format(chart.data.datasets[0].data[ lockedDateIndex ])

    ctx.save()
    ctx.beginPath()
    ctx.font = '600 .75rem "Open Sans", sans-serif'
    ctx.textAlign = 'center' 
    ctx.textBaseline = 'middle'
    ctx.fillStyle = '#294289'
    ctx.fillText(price, priceRectX + (priceRectWidth / 2), priceRectHeight + 23)
    ctx.stroke()
    ctx.restore()
  }
}
